import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useApi } from "../utils/Api";
import "./OrderTracking.css";
import foodImage from "../assets/gallery3.avif";

const WHATSAPP_NUMBER = "447368325136";

export default function OrderTracking() {
  const { apiFetch } = useApi();

  const [status, setStatus] = useState("to do");
  const [orderId, setOrderId] = useState("");
  const [orderType, setOrderType] = useState("");
  const [statusSource, setStatusSource] = useState("local");
  const [driver, setDriver] = useState(null);
  const [eta, setEta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchParams] = useSearchParams();
  const encryptedId = searchParams.get("id");

  const formatApiStatus = (value) => {
    return String(value || "")
      .replace(/[_-]+/g, " ")
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const formatEtaOnlyMinutes = (value) => {
    if (!value) return "";

    const str = String(value).trim();
    const firstPart = str.split(",")[0]?.trim();

    if (/min/i.test(firstPart)) {
      return firstPart;
    }

    return firstPart || str;
  };

 
  useEffect(() => {
    if (!encryptedId) {
      setError("Missing order ID in URL");
      setLoading(false);
      return;
    }

    let interval = null;
    let isMounted = true;
    let inFlight = false;
    let firstLoad = true;

    async function fetchStatus(forceRefresh = false) {
      if (inFlight) return;
      inFlight = true;

      try {
        const data = await apiFetch("order-status", {
          encrypted_order_id: encryptedId,
          force_refresh: forceRefresh ? 1 : 0,
        });

        if (!isMounted) return;

        if (!data.success || !data.order) {
          if (firstLoad) {
            setError(data.message || "Invalid order");
            setLoading(false);
          }
          return;
        }

        const order = data.order;
        const rawStatus = String(order.status || "").trim();
        const normalizedOrderType = String(order.order_type || "")
          .toLowerCase()
          .trim();
        const normalizedStatusSource = String(order.status_source || "local")
          .toLowerCase()
          .trim();

        setStatus(rawStatus);
        setOrderId(order.id || "");
        setOrderType(normalizedOrderType);
        setStatusSource(normalizedStatusSource);
        setDriver(order.driver || null);
        setEta(order.eta || null);
        setLoading(false);
        setError(null);
        firstLoad = false;

        const lowerStatus = rawStatus.toLowerCase();

        const shouldStopPolling =
          lowerStatus === "cancelled" ||
          (normalizedOrderType === "delivery"
            ? lowerStatus === "delivered" ||
              lowerStatus === "done" ||
              lowerStatus === "completed"
            : lowerStatus === "completed");

        if (shouldStopPolling && interval) {
          clearInterval(interval);
        }
      } catch (err) {
        if (!isMounted) return;

        console.error("Failed to fetch order:", err);

        if (firstLoad) {
          setError("Network or server error");
          setLoading(false);
        }
      } finally {
        inFlight = false;
        firstLoad = false;
      }
    }

    fetchStatus(true);
    interval = setInterval(() => fetchStatus(false), 10000);

    return () => {
      isMounted = false;
      if (interval) clearInterval(interval);
    };
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encryptedId]);

  const isDelivery = orderType === "delivery";
  const lowerStatus = String(status || "").toLowerCase().trim();

  const showOriginalThreeStepDesign =
    !isDelivery ||
    (isDelivery && (statusSource === "local" || statusSource === "handoff"));

  const steps = [
    {
      key: "to do",
      label: "Order Received",
      subtext: "Your order is in queue",
    },
    {
      key: "in progress",
      label: "Being Prepared",
      subtext: "Your order is being prepared.",
    },
    {
      key: "completed",
      label:
        isDelivery && statusSource === "handoff"
          ? "Awaiting Rider Pickup"
          : "Order Ready",
      subtext:
        isDelivery && statusSource === "handoff"
          ? "Your order is ready and awaiting rider pickup."
          : isDelivery
            ? "Your order is ready for delivery."
            : "Your order is ready for collection.",
    },
  ];

  const originalTrackingConfig = useMemo(() => {
    const stepOrder = {
      "to do": 0,
      "in progress": 1,
      completed: 2,
    };

    const currentStep = stepOrder[lowerStatus] ?? 0;

    const progressMap = {
      "to do": "20%",
      "in progress": "60%",
      completed: "100%",
    };

    return {
      currentStep,
      progress: progressMap[lowerStatus] || "20%",
    };
  }, [lowerStatus]);

  const showDriverDetails =
    isDelivery &&
    statusSource === "naijameals" &&
    lowerStatus !== "done" &&
    lowerStatus !== "completed" &&
    (driver || eta?.pickup || eta?.dropoff);

  if (loading) {
    return (
      <div className="tracking-page">
        <div className="tracking-loader">Loading order status...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tracking-page">
        <div className="tracking-error">Error: {error}</div>
      </div>
    );
  }

  if (lowerStatus === "cancelled") {
    return (
      <div className="tracking-page">
        <header className="tracking-header cancelled-header">
          <h1 className="cancelled-title">
            <i className="fas fa-times-circle"></i> Order Cancelled
          </h1>
        </header>

        <p className="package-id">
          Order ID: <span>{orderId}</span>
        </p>

        <div className="cancelled-banner">
          This order will not be processed.
        </div>

        <a
          href={`https://wa.me/${WHATSAPP_NUMBER}`}
          target="_blank"
          rel="noopener noreferrer"
          className="whatsapp-support-link"
        >
          <i className="fab fa-whatsapp"></i>
          Click to chat with us on WhatsApp
        </a>

        <div className="order-image-card">
          <img src={foodImage} alt="Order" />
        </div>
      </div>
    );
  }

  return (
    <div className="tracking-page">
      <header className="tracking-header">
        <h1>Order ID: {orderId}</h1>
      </header>

      {showOriginalThreeStepDesign && (
        <>
          <div className="progress-bar-container">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: originalTrackingConfig.progress }}
              />
            </div>
          </div>

          <div className="status-card">
            {steps.map((step, index) => (
              <div
                key={step.key}
                className={`status-step ${
                  index <= originalTrackingConfig.currentStep ? "active" : ""
                }`}
              >
                <span className="dot" />
                <div>
                  <p>{step.label}</p>
                  <small className="status-step-subtext">{step.subtext}</small>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {isDelivery &&
        statusSource === "naijameals" &&
        !showOriginalThreeStepDesign && (
          <div className="status-card">
            <div className="status-step active">
              <span className="dot" />
              <div>
                <p>{formatApiStatus(status)}</p>
                {lowerStatus !== "done" && lowerStatus !== "completed" && (
                  <small className="status-step-subtext">
                    Your delivery is being tracked.
                  </small>
                )}
              </div>
            </div>
          </div>
        )}

      {showDriverDetails && (
        <div className="tracking-info-card tracking-details-card">
          <div className="tracking-card-heading">
            <div className="tracking-card-heading-icon">
              <i className="fas fa-truck"></i>
            </div>
            <h3>Delivery Details</h3>
          </div>

          {driver ? (
            <div className="tracking-detail-list">
              <div className="tracking-detail-row">
                <div className="tracking-detail-icon">
                  <i className="fas fa-user"></i>
                </div>
                <div className="tracking-detail-text">
                  <span>Rider Name</span>
                  <strong>{driver.name || "Not assigned yet"}</strong>
                </div>
              </div>

              <div className="tracking-detail-row">
                <div className="tracking-detail-icon">
                  <i className="fas fa-phone-alt"></i>
                </div>
                <div className="tracking-detail-text">
                  <span>Phone</span>
                  <strong>
                    {driver.phone ? (
                      <a href={`tel:${driver.phone}`}>{driver.phone}</a>
                    ) : (
                      "Not available"
                    )}
                  </strong>
                </div>
              </div>

              <div className="tracking-detail-row">
                <div className="tracking-detail-icon">
                  <i className="fas fa-bicycle"></i>
                </div>
                <div className="tracking-detail-text">
                  <span>Vehicle</span>
                  <strong>{driver.transport_type || "N/A"}</strong>
                </div>
              </div>

              {eta?.pickup && (
                <div className="tracking-detail-row">
                  <div className="tracking-detail-icon">
                    <i className="fas fa-store"></i>
                  </div>
                  <div className="tracking-detail-text">
                    <span>Estimated rider pick-up</span>
                    <strong>{formatEtaOnlyMinutes(eta.pickup)}</strong>
                  </div>
                </div>
              )}

              {eta?.dropoff && (
                <div className="tracking-detail-row">
                  <div className="tracking-detail-icon">
                    <i className="fas fa-clock"></i>
                  </div>
                  <div className="tracking-detail-text">
                    <span>Estimated delivery</span>
                    <strong>{formatEtaOnlyMinutes(eta.dropoff)}</strong>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="tracking-empty-state">
              <i className="fas fa-user-clock"></i>
              <p>Driver has not been assigned yet.</p>
            </div>
          )}

          <div className="tracking-powered-by tracking-powered-by-corner">
            Powered by{" "}
            <a
              href="https://naijameals.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              Naijameals.com
            </a>
          </div>
        </div>
      )}

      {isDelivery &&
        (lowerStatus === "delivered" ||
          lowerStatus === "done" ||
          lowerStatus === "completed") && (
          <div className="ready-banner">Your order has been delivered.</div>
        )}

      {!isDelivery && lowerStatus === "completed" && (
        <div className="ready-banner">Your order is ready for collection.</div>
      )}

      <a
        href={`https://wa.me/${WHATSAPP_NUMBER}`}
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-support-link"
      >
        <i className="fab fa-whatsapp"></i>
        Click to chat with us on WhatsApp
      </a>

      <div className="order-image-card">
        <img src={foodImage} alt="Order" />
      </div>
    </div>
  );
}